import enTranslations from './locales/en.json';
import ruTranslations from './locales/ru.json';
import ukTranslations from './locales/uk.json';
import esTranslations from './locales/es.json';

// Supported languages
export type Language = 'en' | 'ru' | 'uk' | 'es';

export const SUPPORTED_LANGUAGES: { code: Language; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
];

// All translations
const allTranslations: Record<Language, typeof enTranslations> = {
  en: enTranslations,
  ru: ruTranslations,
  uk: ukTranslations,
  es: esTranslations,
};

// Current language state (can be replaced with context/store)
let currentLanguage: Language = 'en';

// Get current language from localStorage or Telegram
export const initLanguage = (): Language => {
  // Try localStorage first
  const stored = localStorage.getItem('app_language');
  if (stored && isValidLanguage(stored)) {
    currentLanguage = stored as Language;
    return currentLanguage;
  }

  // Try Telegram WebApp language
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp;
    const tgLang = tg?.initDataUnsafe?.user?.language_code;
    if (tgLang) {
      // Map language codes (Telegram uses ISO 639-1)
      const langMap: Record<string, Language> = {
        'ru': 'ru',
        'uk': 'uk',
        'es': 'es',
        'en': 'en',
      };
      const mapped = langMap[tgLang.toLowerCase()];
      if (mapped) {
        currentLanguage = mapped;
        localStorage.setItem('app_language', mapped);
        return currentLanguage;
      }
    }
  } catch {
    // Telegram not available
  }

  // Default to English
  currentLanguage = 'en';
  return currentLanguage;
};

// Check if language is valid
export const isValidLanguage = (lang: string): boolean => {
  return SUPPORTED_LANGUAGES.some(l => l.code === lang);
};

// Get current language
export const getCurrentLanguage = (): Language => currentLanguage;

// Set language
export const setLanguage = (lang: Language): void => {
  if (isValidLanguage(lang)) {
    currentLanguage = lang;
    localStorage.setItem('app_language', lang);
  }
};

// Get translations for current language
export const getTranslations = (): typeof enTranslations => {
  return allTranslations[currentLanguage] || enTranslations;
};

// Simple translation function
export const translations = getTranslations();

// Helper function to get nested translation keys
export const t = (key: string): string => {
  const keys = key.split('.');
  let value: unknown = allTranslations[currentLanguage] || enTranslations;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      // Fallback to English
      let fallback: unknown = enTranslations;
      for (const fk of keys) {
        if (fallback && typeof fallback === 'object' && fk in fallback) {
          fallback = (fallback as Record<string, unknown>)[fk];
        } else {
          return key;
        }
      }
      return typeof fallback === 'string' ? fallback : key;
    }
  }

  return typeof value === 'string' ? value : key;
};

// Helper to translate with parameters
export const tWithParams = (key: string, params: Record<string, unknown>): string => {
  let translated = t(key);

  Object.keys(params).forEach(param => {
    const regex = new RegExp(`{{${param}}}`, 'g');
    translated = translated.replace(regex, String(params[param]));
  });

  return translated;
};

// Get tag name by key
export const getTagName = (nameKey: string): string => {
  return t(nameKey);
};

// Initialize language on load
initLanguage();
